import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Card, Col, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ComponentField } from '../../../infrastructure/dynamic-forms/ComponentField';
import { AddressEntryFormData } from '../main-address/AddressEntryFormData';
import { AddressRow } from '../main-address/module-form/AddressRow';
import { AddressModal } from '../validator/AddressModal';
import { MarketChangeModal } from '../../locale-management/warning-modal/MarketChangeModal';
import { useModuleStore } from '../../enrollment-process/module-management/model/ModuleStoreHook';
import { useRootStore } from '../../root/RootStoreHooks';
import { useModuleDisplayMode } from '../../enrollment-process/steps-management/components/step-layouts/accordion/ModuleDisplayMode';
import { AddressDisplay } from '../AddressDisplay';
import { YlAddress, AddressEntryType } from '../../../infrastructure/forms/YlAddress';

export interface AddressDisplayProps {
  address: AddressEntryFormData;
  hideStreet3 : boolean;
}

export const ShippingAddressDisplay = observer<AddressDisplayProps>(({ address, hideStreet3 }) => {
  const { t } = useTranslation();

  return (
    <>
      <p>
        <span>{t('Country')}: </span>
        <span className="text-primary"> {address.country}</span>
      </p>
      <p>
        <span>{t('Address')}: </span>
          {address.country === 'JP' && 
            <span className="text-primary">
              {address.postalCode && <span>{address.postalCode}</span>}
              {address.stateProv && <span>, {address.stateProv}</span>}
              {address.city && <span> {address.city}</span>}
              {address.street1}
              {address.street2 && <span> {address.street2}</span>}
            </span>
          }
          {address.country !== 'JP' && 
            <span className="text-primary">
              {address.street1}
              {address.street2 && <span> {address.street2}</span>}
              {address.street3 && !hideStreet3 && <span> {address.street3}</span>}
              {address.city && <span> {address.city}</span>}
              {address.stateProv && <span> {address.stateProv}</span>}
              {address.locationId && <span>, {address.locationId}</span>}
              {address.postalCode && <span>, {address.postalCode}</span>}
              {address.phoneNumber && <span>, {address.phoneNumber}</span>}
            </span>
          }
      </p>
      {address.isPickupPoint && (
        <p>
          <span>{t('PickupPoint_Recipient')}: </span>
          <span className="text-primary">
            {address.recipient && <span>{address.recipient}</span>}
            {address.phoneNumber && <span>, {address.phoneNumber}</span>}
          </span>
        </p>
      )}
    </>
  );
});

export const RecipientInfoDisplay = observer<AddressDisplayProps>(({ address }) => {
  const { t } = useTranslation();
  return (
    <Card bg="light" border="light" style={{ width: '21rem' }}>
      <Card.Body>
        <Card.Title>{t('PickupPoint_Recipient')}</Card.Title>
        <div>
          {address.recipient && <div>{address.recipient}</div>}
          {address.phoneNumber && <div>{address.phoneNumber}</div>}
        </div>
      </Card.Body>
    </Card>
  );
});

export const ShippingAddressEntry = observer(() => {
  const { t } = useTranslation();
  const rootStore = useRootStore();
  const addressEntry = useModuleStore('shippingAddressEntry');
  const moduleDisplayMode = useModuleDisplayMode();

  function getCustomerAttributes(field: ComponentField) {
    const attr = {} as any;
    if (field.maxLength) {
      attr.maxLength = field.maxLength;
    }

    if (field.minLength) {
      attr.minLength = field.minLength;
    }

    return attr;
  }

  if (!addressEntry) {
    return <Spinner animation="border" variant="secondary" />;
  }

  if (moduleDisplayMode) {
    return <ShippingAddressDisplay address={addressEntry.addressForDisplay} hideStreet3/>;
  }

  return (
    <Form>
      {addressEntry.displayingWillCall && (
        <Form.Group>
          <AddressDisplay address={addressEntry.willCallAddress!} hideStreet3 />
        </Form.Group>
          )}
      {addressEntry.displayingAusPost && (
        <Form.Group>
          <AddressDisplay address={addressEntry.ausPostAddress!} hideStreet3/>
        </Form.Group>
          )}
      {addressEntry.displayingShipToShop && (
        <Form.Group>
          <AddressDisplay address={addressEntry.shipToShopAddress!} hideStreet3/>
        </Form.Group>
          )}
      {addressEntry.displayingPickupPoint && (
        <>
          <Form.Row>
            <Form.Group as={Col} md={6}>
              <AddressDisplay address={addressEntry.pickupPointAddress!} hideStreet3/>
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <RecipientInfoDisplay address={addressEntry.pickupPointAddress!} hideStreet3/>
            </Form.Group>
          </Form.Row>
          <Form.Group>
            <Button variant="outline-primary" size="sm" onClick={() => addressEntry.changePickupPoint()}>
              {t('PickupPoint_Change')}
            </Button>
          </Form.Group>
        </>
      )}
      {!addressEntry.showAusPostWidget && !addressEntry.displayingWillCall && !addressEntry.displayingPickupPoint && !addressEntry.displayingShipToShop && (
        <>
          {addressEntry.askIfSameAsPrimaryAddress && (
            <>
              <Form.Group>
                <Form.Check
                  id="sameAsPrimary"
                  custom
                  label={t('ShippingAddressSameAsPrimary')}
                  type="checkbox"
                  checked={addressEntry.sameAsPrimaryAddress}
                  onChange={() => addressEntry.toggleSameAsPrimaryAddress()}
                />
              </Form.Group>
              {addressEntry.sameAsPrimaryAddress && (
                <Form.Group>
                  <AddressDisplay address={addressEntry.primaryAddress!} hideStreet3/>
                </Form.Group>
              )}
            </>
          )}

            {!addressEntry.showAusPostWidget && !addressEntry.sameAsPrimaryAddress &&
              addressEntry.form.rows.map((row: AddressRow, index: number) => (
                // eslint-disable-next-line react/no-array-index-key
                
            <YlAddress row={row}
                        index={index}
                        form={addressEntry.reactForm}
                        entryComponent={addressEntry}
                        entryType={AddressEntryType.ShippingEntry}
                        countryIso={addressEntry.reactForm.$.country.value}
                        />
              
            ))}
          {addressEntry.triggerCountryChangeAlert && (
            <MarketChangeModal
              showWarning={addressEntry.showWarning}
              hide={addressEntry.cancelCountryChange}
              agreeAndContinue={rootStore.cancelEnrollment}
              country={addressEntry.shippingAddress.country}
              language={undefined}
            />
          )}

          <AddressModal store={addressEntry.addressValidator} />
        </>
      )}
    </Form>
  );
});
